import type { Player } from '../../types/player';
import type { LeagueConfig } from '../../types/league';

/**
 * 2QB League Formula
 * Most QB-premium format - MUST start 2 QBs
 * Based on market data showing ~12 QBs in top 24 picks
 */

export interface TwoQbFormulaWeights {
  previousPPG: number;
  projectedPoints: number;
  qbPremium: number;        // Higher QB premium than Superflex
  qbFloor: number;          // QB weekly floor critical
  rushingQbBonus: number;   // Dual-threat QB premium
  consistency: number;
  targetShare: number;
  positionScarcity: number; 
  teamOffense: number;
  injuryRisk: number;
  ageDecline: number;
}

export const TWOQB_WEIGHTS: TwoQbFormulaWeights = {
  previousPPG: 0.20,        // Proven production
  projectedPoints: 0.22,    // Current projections weighted higher
  qbPremium: 0.20,          // Higher QB premium than SF
  qbFloor: 0.15,            // QB floor very important
  rushingQbBonus: 0.08,     // Rushing QBs get boost
  consistency: 0.06,        // Less important than QB factors
  targetShare: 0.05,        // Less important than QB factors
  positionScarcity: 0.02,   // Let QB premium handle this
  teamOffense: 0.01,        // Minimal weight
  injuryRisk: 0.005,        // Minimal weight
  ageDecline: 0.005         // Minimal weight in redraft 2QB
};

// Market-based replacement levels (based on 12-team 2QB ADP data)
const TWOQB_REPLACEMENT_LEVELS = {
  QB: 24,  // QB24 is replacement (need 24 starting QBs)
  RB: 28,  // RB28 is replacement (less valuable than 1QB)
  WR: 34,  // WR34 is replacement (less valuable than 1QB)
  TE: 14,  // TE14 is replacement
  K: 12,   // K12 is replacement
  DEF: 12  // DEF12 is replacement
};

export function calculateTwoQbValue(
  player: Player,
  leagueConfig: LeagueConfig
): number {
  const weights = TWOQB_WEIGHTS;
  
  // Base factors
  const previousPPG = (player.lastYearPoints || 0) / 17;
  const projectedPoints = player.projection || player.projectedPoints || 0;
  const qbPremium = calculateQBPremium(player);
  const qbFloor = calculateQBFloor(player);
  const rushingQbBonus = calculateRushingQBBonus(player);
  const consistency = calculateConsistency(player);
  const targetShare = calculateTargetShare(player);
  const positionScarcity = calculateTwoQbScarcity(player);
  const teamOffense = calculateTeamOffense(player);
  const injuryRisk = calculateInjuryRisk(player);
  const ageDecline = calculateAgeDecline(player);
  
  // Calculate base value
  let twoQbValue = 
    (weights.previousPPG * previousPPG) +
    (weights.projectedPoints * projectedPoints) +
    (weights.qbPremium * qbPremium) +
    (weights.qbFloor * qbFloor) +
    (weights.rushingQbBonus * rushingQbBonus) +
    (weights.consistency * consistency) +
    (weights.targetShare * targetShare) +
    (weights.positionScarcity * positionScarcity) +
    (weights.teamOffense * teamOffense) +
    (weights.injuryRisk * injuryRisk) +
    (weights.ageDecline * ageDecline);
  
  // Apply WAR (Wins Above Replacement) methodology
  const warMultiplier = calculateWAR(player, projectedPoints);
  twoQbValue *= warMultiplier;
  
  // Market sanity check - prevent too many QBs in top 24 (target: 12)
  if (player.position === 'QB') {
    twoQbValue *= getQBSaturationMultiplier(player);
  }
  
  return twoQbValue;
}

function calculateQBPremium(player: Player): number {
  if (player.position !== 'QB') return 0;
  
  // 2QB format has the highest QB premium - MUST start 2 QBs
  const qbTier = getQBTier(player);
  
  if (qbTier <= 6) return 2.0;      // Elite QBs - massive premium
  if (qbTier <= 12) return 1.8;     // Good QBs - high premium  
  if (qbTier <= 18) return 1.5;     // Startable QBs - moderate premium
  return 1.2;                       // Backup QBs - small premium
}

function calculateQBFloor(player: Player): number {
  if (player.position !== 'QB') return 0;
  
  // QBs with high floors get bonus (rushing ability, team commitment)
  const rushingYards = 400; // Mock - would use real data
  const passingAttempts = 550; // Mock - would use real data
  
  const floorScore = Math.min(1.5, (rushingYards / 400) + (passingAttempts / 600));
  return floorScore;
}

function calculateRushingQBBonus(player: Player): number {
  if (player.position !== 'QB') return 0;
  
  // Bonus for dual-threat QBs (critical in 2QB)
  const rushingQBs = ['Josh Allen', 'Jalen Hurts', 'Lamar Jackson', 'Jayden Daniels', 'Anthony Richardson'];
  
  if (rushingQBs.some(name => player.name.includes(name.split(' ')[1]))) {
    return 1.5; // 50% bonus for rushing QBs
  }
  
  return 1.0;
}

function calculateTwoQbScarcity(player: Player): number {
  // Moderate scarcity multipliers - QB premium handled elsewhere
  const scarcity = {
    QB: 1.0,  // Let QB premium handle this
    RB: 1.2,  // Still valuable despite QB focus
    WR: 1.15, // Still valuable despite QB focus
    TE: 1.3,  // Elite TEs very valuable
    K: 0.6,   // Much less important
    DEF: 0.65 // Much less important
  };
  
  return scarcity[player.position as keyof typeof scarcity] || 1.0;
}

function calculateWAR(player: Player, projectedPoints: number): number {
  const position = player.position;
  const replacementLevel = TWOQB_REPLACEMENT_LEVELS[position as keyof typeof TWOQB_REPLACEMENT_LEVELS] || 24;
  
  // Calculate points above replacement
  const replacementPoints = getReplacementPoints(position);
  const pointsAboveReplacement = Math.max(0, projectedPoints - replacementPoints);
  
  // Convert to WAR multiplier (diminishing returns)
  const warMultiplier = 1.0 + (pointsAboveReplacement / 120); // Adjusted for 2QB
  
  return Math.min(2.5, warMultiplier); // Cap at 2.5x
}

function getReplacementPoints(position: string): number {
  // Estimated replacement-level points by position (2QB context)
  const replacementPoints = {
    QB: 260,  // QB24 level (lower than 1QB due to scarcity)
    RB: 160,  // RB28 level
    WR: 140,  // WR34 level
    TE: 110,  // TE14 level
    K: 95,    // K12 level
    DEF: 85   // DEF12 level
  };
  
  return replacementPoints[position as keyof typeof replacementPoints] || 100;
}

function getQBSaturationMultiplier(player: Player): number {
  // Prevent QB saturation - target ~12 QBs in top 24
  const qbRank = getQBTier(player);
  
  if (qbRank <= 6) return 1.0;      // Elite QBs - no penalty
  if (qbRank <= 12) return 0.95;    // Good QBs - tiny penalty
  if (qbRank <= 18) return 0.8;     // Mid-tier QBs - moderate penalty
  return 0.6;                       // Lower-tier QBs - significant penalty
}

function getQBTier(player: Player): number {
  // Mock QB ranking - would use real consensus rankings
  const tierOneQBs = ['Josh Allen', 'Lamar Jackson', 'Jalen Hurts'];
  const tierTwoQBs = ['Joe Burrow', 'Jayden Daniels', 'Dak Prescott', 'Tua Tagovailoa', 'Brock Purdy', 'CJ Stroud'];
  const tierThreeQBs = ['Kyler Murray', 'Aaron Rodgers', 'Trevor Lawrence', 'Jordan Love', 'Caleb Williams', 'Anthony Richardson'];
  
  if (tierOneQBs.some(name => player.name.includes(name.split(' ')[1]))) return 1;
  if (tierTwoQBs.some(name => player.name.includes(name.split(' ')[1]))) return 4;
  if (tierThreeQBs.some(name => player.name.includes(name.split(' ')[1]))) return 8;
  
  return 15; // Default lower tier
}

// Helper functions (shared with other formulas)
function calculateConsistency(player: Player): number {
  const baseConsistency = { QB: 0.9, RB: 0.6, WR: 0.5, TE: 0.7, K: 0.4, DEF: 0.5 };
  return baseConsistency[player.position as keyof typeof baseConsistency] || 0.5;
}

function calculateTargetShare(player: Player): number {
  const baseShares = { QB: 1.0, RB: 0.15, WR: 0.20, TE: 0.15, K: 0, DEF: 0 };
  return baseShares[player.position as keyof typeof baseShares] || 0;
}

function calculateTeamOffense(player: Player): number {
  const strongOffenses = ['BUF', 'KC', 'SF', 'DAL', 'MIA', 'CIN'];
  return strongOffenses.includes(player.team) ? 1.15 : 1.0;
}

function calculateInjuryRisk(player: Player): number {
  const positionRisk = { QB: 0.9, RB: 0.7, WR: 0.8, TE: 0.8, K: 0.95, DEF: 0.9 };
  const ageRisk = (player.age || 26) > 30 ? 0.85 : 1.0;
  return (positionRisk[player.position as keyof typeof positionRisk] || 0.8) * ageRisk;
}

function calculateAgeDecline(player: Player): number {
  const age = player.age || 26;
  if (age < 24) return 1.02; // Very small youth bonus in redraft 2QB
  if (age < 27) return 1.0;  // Prime
  if (age < 30) return 0.99; // Tiny decline
  return 0.97; // Small decline (less than standard)
}

export function getTwoQbPositionPriority(): string[] {
  // Based on market data: QB heavy early, then skill positions
  return ['QB', 'QB', 'RB', 'WR', 'QB', 'RB', 'WR', 'TE'];
}

export function getTwoQbDraftStrategy(): string[] {
  return [
    'MUST secure 2 starting QBs in first 4-5 rounds',
    'Elite rushing QBs (Allen, Hurts, Lamar) are tier 1 assets',
    'Consider 3rd QB for bye weeks and injury insurance',
    'Zero-RB strategy becomes more viable with QB scarcity',
    'Elite skill players still valuable but not at QB expense',
    'Wait on K/DEF - points come from starting positions',
    'Target high-floor QBs over boom-bust skill players early'
  ];
}